/**
 * Captura do evento `beforeinstallprompt` ANTES de React montar.
 *
 * Por que aqui e não no bundle do app: o evento dispara UMA vez quando o
 * Chromium decide que a página é elegível pra install (SW + manifest + HTTPS +
 * engagement). Em conexão rápida, o evento chega antes do AppLoader resolver
 * o bundle + AppRoot montar + `useInstallPrompt` adicionar listener. Sem
 * captura cedo, o evento se perde e `promptInstall()` nunca funciona.
 *
 * Ver G61 em `.claude/skills/hook-conversion-shared/catalog/known-gotchas.md`.
 * Pattern portado de `hook-old/src/main.tsx:15-20` (production-tested).
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface Window {
    __pwaInstallPrompt?: BeforeInstallPromptEvent | null;
    __pwaInstallPromptCapturedAt?: number;
    posthog?: { capture?: (event: string, props?: Record<string, unknown>) => void };
  }
}

/**
 * Registra listeners globais de install lifecycle. Chamar UMA vez, em
 * `main.tsx`, ANTES de `ReactDOM.createRoot`. Idempotente (remove listeners
 * antigos se for re-chamada, ex: HMR em dev).
 */
export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;

  // Remove listeners anteriores se já existirem (HMR / re-entry)
  if (captured) {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
  }
  captured = true;

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

let captured = false;

function onBeforeInstallPrompt(e: Event): void {
  // Sem preventDefault, Chromium mostra seu próprio mini-infobar nativo que
  // compete com nosso splash. Reservamos o controle da UX.
  e.preventDefault();
  window.__pwaInstallPrompt = e as BeforeInstallPromptEvent;
  window.__pwaInstallPromptCapturedAt = Date.now();

  const slug = inferSlugFromPath();
  window.posthog?.capture?.('pwa_install_prompt_available', {
    slug,
    captured_at: window.__pwaInstallPromptCapturedAt,
  });
}

function onAppInstalled(): void {
  window.__pwaInstallPrompt = null;

  const slug = inferSlugFromPath();
  if (slug) {
    try {
      localStorage.setItem(`install:${slug}:installed-at`, new Date().toISOString());
    } catch {
      // storage bloqueado (private mode antigo) — ignore, standalone detect ainda pega
    }
  }
  window.posthog?.capture?.('pwa_installed', { slug });
}

/**
 * Extrai slug do pathname `/app/<slug>/...`. Null se não estiver em rota de app.
 */
function inferSlugFromPath(): string | null {
  if (typeof location === 'undefined') return null;
  const match = /^\/app\/([a-z0-9-]+)\//.exec(location.pathname);
  return match ? match[1] : null;
}
